import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Scrolls the app's content area back to the top when the route changes.
 *
 * Why this exists rather than react-router's `<ScrollRestoration/>`: that
 * component scrolls the WINDOW, and this shell doesn't scroll the window at
 * all — `AppLayout` pins the grid to `100dvh` and puts `overflow-y-auto` on
 * `<main>`, so `<main>` is the scroll container. Dropping `<ScrollRestoration/>`
 * in would compile, run, and silently do nothing.
 *
 * Only forward navigations reset. On a Back/Forward (`POP`) we leave the
 * position alone and `useScrollRestoration` (see `./scroll-restoration`) puts
 * the entry's own offset back — that hook owns `POP`, this one owns everything
 * else, so the two never contend for the same container.
 *
 * Keyed on `pathname` only: changing a query string filters the same page in
 * place, and yanking the viewport upward mid-filter is disorienting.
 */
export function useScrollResetOnNavigate(ref: React.RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    // Skip the first render: the page has just mounted at its natural offset,
    // and SSR-hydrated entries would otherwise jump.
    if (previous.current === pathname) return;
    const isFirstRender = previous.current === null;
    previous.current = pathname;
    if (isFirstRender || navigationType === 'POP') return;

    ref.current?.scrollTo({ top: 0 });
    // Belt and braces: if a future layout lets the window scroll instead, this
    // keeps the behaviour correct rather than silently regressing.
    window.scrollTo({ top: 0 });
  }, [pathname, navigationType, ref]);
}
